<template>
  <div>
    <div class="pull-right" data-cy="download-symbols">
      <div class="symbols">
        <p>Download all symbols</p>
        <ButtonGroup class="download-all-group">
          <Button
            class="download-button"
            type="button"
            :theme-color="'primary'"
            @click="download('OU Symbols eps.zip', 'eps')"
            >EPS
          </Button>

          <Button
            class="download-button"
            type="button"
            :theme-color="'success'"
            @click="download('OU Symbols jpg.zip', 'jpg')"
            >JPG
          </Button>
        </ButtonGroup>
      </div>
    </div>
  </div>

  <div class="symbols-grid" data-cy="symbols-grid">
    <Card v-for="(card, index) in symbolData" :key="index" class="symbols-card">
      <div class="symbols-card-image">
        <img :src="getImageUrl(card.Source)" :alt="card.Source" />
      </div>

      <div class="symbols-card-right">
        <ButtonGroup class="symbols-card-buttons">
          <Button
            class="download-button"
            type="button"
            :theme-color="'primary'"
            @click="download(`${card.Source}.eps`, 'eps')"
            >EPS
          </Button>

          <Button
            class="download-button"
            type="button"
            :theme-color="'success'"
            @click="download(`${card.Source}.jpg`, 'jpg')"
            >JPG
          </Button>
        </ButtonGroup>

        <div class="symbols-card-desc">
          {{ card.Description }}
          <b>{{ card.DescBold }}</b>
        </div>
      </div>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { Card } from "@progress/kendo-vue-layout";
import { useMainStore } from "@/stores/useMainStore";
import { Button, ButtonGroup } from "@progress/kendo-vue-buttons";

const main = useMainStore();

const symbolData = [
  {
    Source: "OU-parevehoriz",
    Description:
      "A Kosher product that is neutral, does not contain dairy or meat ingredients / derivatives, and is not processed on dairy or meat equipment.",
  },
  {
    Source: "OU-bodycopy-logo",
    Description:
      "A Kosher product that is neutral, does not contain dairy or meat ingredients / derivatives, and is not processed on dairy or meat equipment.",
  },
  {
    Source: "OU-D",
    Description:
      "A Kosher product that contains dairy ingredients / derivatives, or is processed on dairy equipment.",
  },
  {
    Source: "OU-Dairyhoriz",
    Description:
      "A Kosher product that contains dairy ingredients / derivatives, or is processed on dairy equipment.",
  },
  {
    Source: "OU-PassoverDairy",
    Description:
      "A Kosher product that is certified for Passover use, and has an OUD status.",
  },
  {
    Source: "OU-Dairyvert",
    Description:
      "A Kosher product that contains dairy ingredients / derivatives, or is processed on dairy equipment.",
  },
  {
    Source: "OU-Dfishvert",
    Description:
      "A Kosher product that contains fish ingredients / derivatives, and has an OUD status.",
  },
  {
    Source: "OU-DairyFishvert",
    Description:
      "A Kosher product that contains fish ingredients / derivatives, and has an OUD status.",
  },
  {
    Source: "OU-fishhoriz",
    Description:
      "A Kosher product that contains fish ingredients / derivatives, and has an OU status.",
  },
  {
    Source: "OU-fishvert",
    Description:
      "A Kosher product that contains fish ingredients / derivatives, and has an OU status.",
  },
  {
    Source: "OU-glatthoriz",
    Description:
      "A Kosher product that is meat, or contains meat ingredients / derivatives, or is processed on meat equipment.",
  },
  {
    Source: "OU-glattvert",
    Description:
      "A Kosher product that is meat, or contains meat ingredients / derivatives, or is processed on meat equipment.",
  },
  {
    Source: "OU-PassoverGlatt",
    Description:
      "A Kosher product that is certified for Passover use, and has an OU Glatt status.",
  },
  {
    Source: "OU-kitniyot",
    Description:
      "A Kosher product that has a special type of Passover certification because it contains legumes. (There are two customs within the Jewish community; some consume legumes on Passover and others do not.)",
  },
  {
    Source: "OU-kitniyotHE",
    Description:
      "A Kosher product that has a special type of Passover certification because it contains legumes. (There are two customs within the Jewish community; some consume legumes on Passover and others do not.)",
  },
  {
    Source: "OU-Passover",
    Description:
      "A Kosher product that is certified for Passover use, and has an OU status.",
  },
  {
    Source: "OU-kfphoriz",
    Description:
      "A Kosher product that is certified for Passover use, and has an OU status.",
  },
  {
    Source: "OU-kfpvert",
    Description:
      "A Kosher product that is certified for Passover use, and has an OU status.",
  },
  {
    Source: "OU-PassoverFish",
    Description:
      "A Kosher product that is certified for Passover use, and has an OU Fish status.",
  },
  {
    Source: "OU-OuDe",
    Description:
      "Products made on dairy equipment which do not contain dairy ingredients may be eligible for OUDE status if special criteria are met.",
  },
  {
    Source: "OU-proud",
    Description: "This file is for marketing purposes only and ",
    DescBold: "may not be used on product labels.",
  },
];

const download = function (fileName: string, type: string) {
  api.getDownload("GetTrademarkFile", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    fileName: fileName,
    type: type,
  });
};

const getImageUrl = function (source: string) {
  return new URL(`/src/assets/img/trademarks/${source}.jpg`, import.meta.url).href;
};
</script>

<style scoped lang="scss">
.k-card-header {
  width: 200px;
  padding: 0px;
}

.pull-right {
  display: flex;
  margin-left: auto;
  gap: 8px;
}

.download-all-group {
  display: flex;
  min-width: 200px;
}

.download-button {
  flex-grow: 1;
}

.symbols-grid {
  display: flex;
  justify-content: space-evenly;
  flex-wrap: wrap;
  gap: 12px;
}

.symbols-card.k-card {
  display: flex;
  box-shadow: 0px 0px 0px 0px rgba(0, 0, 0, 0.1);
  flex-direction: row;
  flex-basis: 100%;

  @include media-breakpoint-up(md) {
    flex-basis: calc(50% - 6px);
  }

  @include media-breakpoint-up(xl) {
    flex-basis: calc(33.33% - 8px);
  }

  &-header {
    background: transparent;
  }
}

.symbols-card-image {
  min-width: unset;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  @include media-breakpoint-up(sm) {
    min-width: 175px;
  }

  & img {
    max-width: 120px;

    @include media-breakpoint-up(sm) {
      max-width: 100%;
    }
  }
}

.symbols-card-buttons.k-button-group {
  display: flex;
  justify-content: center;
  font-family: "museo-sans", helvetica, arial, sans-serif;
  font-size: 13px;
  padding: 15px;
}

.symbols-card-desc {
  font-family: "museo-sans", helvetica, arial, sans-serif;
  font-size: 13px;
  padding: 15px 8px;
}
.symbols {
  display: flex;
  align-items: center;
  p {
    font-size: 16px;
    font-weight: bold;
    margin-right: 10px;
  }
}
</style>
